// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — reading an organisation's own layer from a file (Situation room,
 * slice CR5, files only): GeoJSON (`.geojson`, `.json`), KML (`.kml`) or a
 * zipped KML (`.kmz`). KML is turned into GeoJSON here, in the browser, so
 * the server only ever stores and checks one format
 * (`situationRoom.checkLayerGeoJson`, which also keeps only each feature's
 * name and description).
 *
 * KML: every Placemark's Point, LineString, Polygon (with holes) and
 * MultiGeometry, wherever it sits in folders; its `name` and `description`
 * (HTML in a description is reduced to its text). Styles, icons, network
 * links, ground overlays and tracks are not read. Written for AYNI from
 * the OGC KML 2.2 structure, without a library.
 */

import { checkLayerGeoJson, type LayerCheck } from './situationRoom'

/** Largest file read (the stored layer is smaller still, after names and descriptions only are kept). */
export const OWN_LAYER_MAX_FILE_BYTES = 20 * 1024 * 1024

export type OwnLayerFileResult =
  | (LayerCheck & { ok: true; title: string })
  | { ok: false; error: 'invalid' | 'empty' | 'too_large' | 'too_many_features' | 'unreadable' | 'not_supported' }

type Geometry = { type: string; coordinates: unknown }

function coordinatesOf(el: Element | null | undefined): number[][] {
  const text = el?.textContent?.trim() ?? ''
  if (!text) return []
  const out: number[][] = []
  for (const tuple of text.split(/\s+/)) {
    const [lon, lat] = tuple.split(',').map(Number)
    if (Number.isFinite(lon) && Number.isFinite(lat)) out.push([lon, lat])
  }
  return out
}

/** The first direct child with this local name (KML may or may not use a namespace prefix). */
function child(el: Element, name: string): Element | null {
  for (const c of Array.from(el.children)) if (c.localName === name) return c
  return null
}

function children(el: Element, name: string): Element[] {
  return Array.from(el.children).filter((c) => c.localName === name)
}

function ring(boundary: Element | null): number[][] | null {
  const linear = boundary ? child(boundary, 'LinearRing') : null
  const coords = coordinatesOf(linear ? child(linear, 'coordinates') : null)
  if (coords.length < 4) return null
  return coords
}

function geometriesOf(el: Element): Geometry[] {
  switch (el.localName) {
    case 'Point': {
      const c = coordinatesOf(child(el, 'coordinates'))
      return c.length ? [{ type: 'Point', coordinates: c[0] }] : []
    }
    case 'LineString': {
      const c = coordinatesOf(child(el, 'coordinates'))
      return c.length >= 2 ? [{ type: 'LineString', coordinates: c }] : []
    }
    case 'Polygon': {
      const outer = ring(child(el, 'outerBoundaryIs'))
      if (!outer) return []
      const holes = children(el, 'innerBoundaryIs').map(ring).filter((r): r is number[][] => r !== null)
      return [{ type: 'Polygon', coordinates: [outer, ...holes] }]
    }
    case 'MultiGeometry':
      return Array.from(el.children).flatMap(geometriesOf)
    default:
      return []
  }
}

/** Several geometries of one kind become one Multi*; mixed kinds stay separate features. */
function merge(geoms: Geometry[]): Geometry[] {
  if (geoms.length <= 1) return geoms
  const kinds = new Set(geoms.map((g) => g.type))
  if (kinds.size !== 1) return geoms
  const kind = geoms[0].type
  return [{ type: `Multi${kind}`, coordinates: geoms.map((g) => g.coordinates) }]
}

/** HTML in a KML description, as text (never parsed into the page). */
function plainText(raw: string): string {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${raw}`, 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/** A KML document as a GeoJSON FeatureCollection (not yet checked), or null when it is not KML. */
export function kmlToGeoJson(text: string): { type: 'FeatureCollection'; features: unknown[]; name: string | null } | null {
  // A CDATA section is literal text: escaped as such before parsing, it reads the same in every parser.
  const escaped = text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (_m, body: string) =>
    body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
  const doc = new DOMParser().parseFromString(escaped, 'application/xml')
  const root = doc.documentElement
  if (!root || root.localName !== 'kml' || doc.getElementsByTagName('parsererror').length > 0) return null
  const features: unknown[] = []
  // By local name, with or without a namespace prefix (`kml:Placemark`).
  const placemarks = Array.from(root.getElementsByTagName('*')).filter((el) => el.localName === 'Placemark')
  for (const pm of placemarks) {
    const geoms = merge(Array.from(pm.children).flatMap(geometriesOf))
    const name = child(pm, 'name')?.textContent?.trim() ?? ''
    const description = plainText(child(pm, 'description')?.textContent ?? '')
    const properties: Record<string, string> = {}
    if (name) properties.name = name
    if (description) properties.description = description
    for (const geometry of geoms) features.push({ type: 'Feature', geometry, properties })
  }
  const docEl = Array.from(root.children).find((c) => c.localName === 'Document' || c.localName === 'Folder')
  const name = docEl ? child(docEl, 'name')?.textContent?.trim() || null : null
  return { type: 'FeatureCollection', features, name }
}

/** A file name without its extension, as a layer's first title. */
function titleFrom(fileName: string, fallback: string | null): string {
  const base = fileName.replace(/\.(geo)?json$|\.km[lz]$/i, '').replace(/[_-]+/g, ' ').trim()
  return (fallback || base || 'Layer').slice(0, 80) // i18n-exempt: last-resort layer title, replaced by the person
}

/** Read an own layer from a file the person picked. */
export async function readOwnLayerFile(file: Blob & { name?: string }): Promise<OwnLayerFileResult> {
  const fileName = file.name ?? ''
  if (file.size > OWN_LAYER_MAX_FILE_BYTES) return { ok: false, error: 'too_large' }
  const ext = /\.([a-z]+)$/i.exec(fileName)?.[1]?.toLowerCase() ?? ''
  let raw: unknown
  let title: string | null = null
  try {
    if (ext === 'kmz') {
      const { default: JSZip } = await import('jszip')
      const zip = await JSZip.loadAsync(await file.arrayBuffer())
      const entry = zip.file('doc.kml') ?? zip.file(/\.kml$/i)[0] ?? null
      if (!entry) return { ok: false, error: 'invalid' }
      const kml = kmlToGeoJson(await entry.async('string'))
      if (!kml) return { ok: false, error: 'invalid' }
      raw = kml
      title = kml.name
    } else if (ext === 'kml') {
      const kml = kmlToGeoJson(await file.text())
      if (!kml) return { ok: false, error: 'invalid' }
      raw = kml
      title = kml.name
    } else if (ext === 'geojson' || ext === 'json') {
      raw = JSON.parse(await file.text())
      const name = (raw as { name?: unknown } | null)?.name
      title = typeof name === 'string' ? name : null
    } else {
      return { ok: false, error: 'not_supported' }
    }
  } catch {
    return { ok: false, error: 'unreadable' }
  }
  const checked = checkLayerGeoJson(raw)
  if (!checked.ok) return checked
  return { ...checked, title: titleFrom(fileName, title) }
}
